import { useMemo, useState } from "react";
import { Button } from "@fluentui/react-components";
import { Copy24Regular, Checkmark24Regular, LightbulbFilament24Regular, ClipboardTextLtr24Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse, docLinkToContentName, getCitationFilePath, SpeechConfig } from "../../api";
import { parseAnswerToHtml, splitOutCodeBlocks } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";
import { MermaidDiagram } from "./MermaidDiagram";
import { Feedback } from "./Feedback";

interface Props {
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
}

export const Answer = ({
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming, onCitationClicked), [answer, isStreaming, onCitationClicked]);
    const { t } = useTranslation();
    // The answer HTML mixes raw Markdown (including fenced code / mermaid blocks)
    // with citation <span> markup. DOMPurify treats the whole string as HTML and
    // would escape characters inside fenced blocks (e.g. mermaid "-->" becomes
    // "--&gt;", "&" becomes "&amp;"), corrupting diagrams. To avoid this, we pull
    // the fenced code blocks out before sanitizing and splice them back in
    // verbatim afterwards, so only the surrounding prose/citation HTML is sanitized.
    const sanitizedAnswerHtml = useMemo(() => {
        const segments = splitOutCodeBlocks(parsedAnswer.answerHtml);
        return segments.map(segment => (segment.isCode ? segment.value : DOMPurify.sanitize(segment.value))).join("");
    }, [parsedAnswer.answerHtml]);
    const [copied, setCopied] = useState(false);

    // Render ```mermaid fenced code blocks as diagrams. While the answer is still
    // streaming the diagram definition is usually incomplete, so we keep showing
    // the raw code and only render the diagram once streaming has finished.
    const markdownComponents = useMemo(
        () => ({
            // Links in answers must never navigate the (embedded) chat itself: many targets
            // refuse to load in an iframe and relative links would point at the chat origin.
            a(props: any) {
                const { href, children, node, ...rest } = props;
                // Citation badges carry no href and are handled elsewhere.
                if (!href) return <a {...rest}>{children}</a>;
                if (/^(https?:\/\/|mailto:)/i.test(href)) {
                    return (
                        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
                            {children}
                        </a>
                    );
                }
                const docName = docLinkToContentName(href);
                if (docName) {
                    return (
                        <a
                            {...rest}
                            href={getCitationFilePath(docName)}
                            onClick={e => {
                                e.preventDefault();
                                onCitationClicked(getCitationFilePath(docName));
                            }}
                        >
                            {children}
                        </a>
                    );
                }
                // Unresolvable relative link: show the text instead of a dead link.
                return <span>{children}</span>;
            },
            code(props: any) {
                const { className, children } = props;
                const code = String(children ?? "");
                // Detect mermaid either via the explicit ```mermaid fence language
                // or heuristically when the model omits the language but the block
                // clearly starts with a mermaid diagram keyword.
                const hasMermaidClass = /\blanguage-mermaid\b/.test(className ?? "");
                const looksLikeMermaid =
                    /^\s*(flowchart|graph|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|gantt|pie|journey|mindmap|timeline|gitGraph|quadrantChart)\b/.test(
                        code
                    );
                if ((hasMermaidClass || looksLikeMermaid) && !isStreaming) {
                    return <MermaidDiagram chart={code} />;
                }
                return <code className={className}>{children}</code>;
            }
        }),
        [isStreaming, onCitationClicked]
    );

    const handleCopy = () => {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = sanitizedAnswerHtml;
        tempElement.querySelectorAll("sup").forEach(node => node.remove());
        tempElement.querySelectorAll(".citationStepBadge").forEach(node => node.remove());
        const textToCopy = tempElement.textContent ?? "";

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    return (
        <div
            className={`${styles.answerContainer} ${isSelected ? styles.selected : ""}`}
            style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}
        >
            <div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <AnswerIcon />
                        <span className={styles.aiGeneratedLabel} title={t("aiGeneratedHint")}>
                            {t("aiGeneratedLabel")}
                        </span>
                    </div>
                    <div>
                        <Button
                            appearance="transparent"
                            style={{ color: "black" }}
                            icon={copied ? <Checkmark24Regular /> : <Copy24Regular />}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            aria-label={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        />
                        <Button
                            appearance="transparent"
                            style={{ color: "black" }}
                            icon={<LightbulbFilament24Regular />}
                            title={t("tooltips.showThoughtProcess")}
                            aria-label={t("tooltips.showThoughtProcess")}
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.context.thoughts?.length || isStreaming}
                        />
                        <Button
                            appearance="transparent"
                            style={{ color: "black" }}
                            icon={<ClipboardTextLtr24Regular />}
                            title={t("tooltips.showSupportingContent")}
                            aria-label={t("tooltips.showSupportingContent")}
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.context.data_points || isStreaming}
                        />
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </div>
            </div>

            <div style={{ flexGrow: 1 }}>
                <div className={styles.answerText}>
                    <ReactMarkdown children={sanitizedAnswerHtml} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} components={markdownComponents} />
                </div>
            </div>

            {!isStreaming && <Feedback traceId={answer.context?.trace_id} />}

            {!!parsedAnswer.citations.length && (
                <div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                        <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                        {parsedAnswer.citations.map(citation => {
                            const isWeb = citation.isWeb;
                            const displayIndex = citation.index;
                            const reference = citation.reference;
                            if (isWeb) {
                                const webEntry = answer.context.data_points.external_results_metadata?.find(w => w.url === reference);
                                const titleOrUrl = webEntry?.title?.trim() ? webEntry.title : reference;
                                return (
                                    <span key={`${reference}-${displayIndex}`} className={styles.citationEntry}>
                                        <a className={styles.citation} title={reference} href={reference} target="_blank" rel="noopener noreferrer">
                                            {`${displayIndex}. ${titleOrUrl}`}
                                        </a>
                                    </span>
                                );
                            } else {
                                const path = getCitationFilePath(reference);
                                return (
                                    <span key={`${reference}-${displayIndex}`} className={styles.citationEntry}>
                                        <a
                                            className={styles.citation}
                                            title={reference}
                                            onClick={e => {
                                                e.preventDefault();
                                                onCitationClicked(path);
                                            }}
                                        >
                                            {`${displayIndex}. ${reference}`}
                                        </a>
                                    </span>
                                );
                            }
                        })}
                    </div>
                </div>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <div>
                    <div
                        style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                        className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`}
                    >
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
